import { ApiService } from "./ApiService";
import { ResultsDTO } from "Types/ResultsDTO";

interface GetAllGlossaryArgs {
  plant: string;
  search?: string;
}

interface GetGlossaryByIdArgs {
  id: number;
  plant: string;
}

interface DeleteGlossaryArgs {
  id: number;
  plant: string;
}

interface BulkDeleteGlossaryArgs {
  ids: number[];
  plant: string;
}

interface DeleteGlossaryImageArgs {
  detId: number;
  plant: string;
}

interface UploadGlossaryImageArgs {
  plant: string;
  files: File[];
  hdrId: number;
}

type UpdateGlossaryArgs =
  | FormData
  | {
      id: number;
      plant: string;
      glossaryName: string;
      glossaryDescription?: string;
      glossaryDescription2?: string;
      glossaryDate: string;
      address?: string;
      city?: string;
      state?: string;
      country?: string;
      pin?: string;
      section1Title?: string;
      section1Description?: string;
      section2Title?: string;
      section2Description?: string;
      section3Title?: string;
      section3Description?: string;
      section4Title?: string;
      section4Description?: string;
      section5Title?: string;
      section5Description?: string;
      section6Title?: string;
      section6Description?: string;
      section7Title?: string;
      section7Description?: string;
      section8Title?: string;
      section8Description?: string;
    };

export const GlossaryApiSlice = ApiService.injectEndpoints({
  endpoints: (builder) => ({
    getAllGlossary: builder.query<ResultsDTO<any[]>, GetAllGlossaryArgs>({
      query: ({ plant, search }) => ({
        url: "/glossary/getAll",
        method: "GET",
        params: { plant, ...(search ? { search } : {}) },
      }),
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({ glossaryHdr }: any) => ({
                type: "Glossary" as const,
                id: glossaryHdr.id,
              })),
              { type: "Glossary", id: "LIST" },
            ]
          : [{ type: "Glossary", id: "LIST" }],
    }),

    getGlossaryById: builder.query<ResultsDTO<any>, GetGlossaryByIdArgs>({
      query: ({ id, plant }) => ({
        url: "/glossary",
        method: "GET",
        params: { id, plant },
      }),
      providesTags: (result, error, { id }) => [{ type: "Glossary", id }],
    }),

    createGlossary: builder.mutation<ResultsDTO, FormData>({
      query: (formData) => ({
        url: "/glossary/create",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: [{ type: "Glossary", id: "LIST" }],
    }),

    updateGlossary: builder.mutation<ResultsDTO, UpdateGlossaryArgs>({
      query: (args) => ({
        url: "/glossary/update",
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: args,
      }),
      invalidatesTags: (result, error, args) => [
        { type: "Glossary", id: "LIST" },
        { type: "Glossary", id: (args as any).id },
      ],
    }),

    deleteGlossary: builder.mutation<ResultsDTO, DeleteGlossaryArgs>({
      query: ({ id, plant }) => ({
        url: "/glossary/delete",
        method: "DELETE",
        params: { id, plant },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Glossary", id: "LIST" },
        { type: "Glossary", id },
      ],
    }),

    bulkDeleteGlossary: builder.mutation<ResultsDTO, BulkDeleteGlossaryArgs>({
      query: ({ ids, plant }) => ({
        url: "/glossary/bulk-delete",
        method: "DELETE",
        params: { ids, plant },
      }),
      invalidatesTags: [{ type: "Glossary", id: "LIST" }],
    }),

    deleteGlossaryImage: builder.mutation<ResultsDTO, DeleteGlossaryImageArgs>({
      query: ({ detId, plant }) => ({
        url: "/glossary/delete-image",
        method: "DELETE",
        params: { detId, plant },
      }),
      invalidatesTags: [{ type: "Glossary", id: "LIST" }],
    }),

    uploadGlossaryImage: builder.mutation<ResultsDTO, UploadGlossaryImageArgs | FormData>({
      query: (args) => {
        if (args instanceof FormData) {
          return {
            url: "/glossary/upload-image",
            method: "POST",
            body: args,
          };
        } else {
          const formData = new FormData();
          formData.append("plant", args.plant);
          formData.append("hdrId", args.hdrId.toString());
          args.files.forEach((file) => { formData.append("files", file); });
          return {
            url: "/glossary/upload-image",
            method: "POST",
            body: formData,
          };
        }
      },
      invalidatesTags: (result, error, args) => [
        { type: "Glossary", id: "LIST" },
        { type: "Glossary", id: args instanceof FormData ? Number(args.get("hdrId")) : args.hdrId },
      ],
    }),
  }),
});

export const {
  useGetAllGlossaryQuery,
  useLazyGetAllGlossaryQuery,
  useGetGlossaryByIdQuery,
  useCreateGlossaryMutation,
  useUpdateGlossaryMutation,
  useDeleteGlossaryMutation,
  useBulkDeleteGlossaryMutation,
  useDeleteGlossaryImageMutation,
  useUploadGlossaryImageMutation,
} = GlossaryApiSlice;
